import { Cross, SwapArrowLeftRight } from "@ds/icons";
import { Button, formatter } from "@ruter-as/web-components-and-tools";
import type React from "react";
import { useNavigate } from "react-router";
import type { Subscription } from "../../api/subscription";
import type { SubscriptionType } from "../../api/subscriptionType";
import type { Ticket } from "../../api/Ticket";
import getSubscriptionText from "../../subscriptionText";
import "./Subscriptions.component.scss";

interface SubscriptionsProps {
	tickets: Ticket[];
	subscriptions: Subscription[];
	isHousingAssociation: boolean;
	hasTravelSubscription: boolean;
}

const humanReadableStatus = (status: string): string => {
	switch (status.toLowerCase()) {
		case "active":
			return "Gyldig";
		case "pending":
			return "Ventende";
		case "cancelled":
			return "Kansellert";
		case "expired":
			return "Utløpt";
		case "failed":
			return "Feilet";
		default:
			return "Ukjent";
	}
};

export const getZonesText = (nrOfZones: number, zones: string[]): string => {
	if (nrOfZones === 1) {
		return `Sone ${zones[0]}`;
	}
	if (nrOfZones === 2) {
		if (zones.length < 2) {
			return "Ukjent";
		}
		const zone1 = zones[0];
		const zone2 = zones[1];
		return `${zone1} og ${zone2}`;
	}
	return "Alle soner";
};

const getSubscriptionType = (type: SubscriptionType): string => {
	switch (type) {
		case "JOULE":
			return "Elsykkel";
		case "CAR_POOL":
			return "Leiebil";
		case "TICKET":
			return "Billett";
		case "TRAVEL_SUBSCRIPTION":
			return "Reiseabonnement";
		case "UNKNOWN":
			return "Ukjent";
	}
};

const SubscriptionComponent: React.FC<SubscriptionsProps> = ({
	tickets,
	subscriptions,
	isHousingAssociation,
	hasTravelSubscription,
}) => {
	const navigate = useNavigate();

	const renderTicket = (ticket: Ticket) => {
		const enableChangeZone =
			ticket.status === "active" && ticket.expirationDate === null;
		const enableCancelTicket =
			(ticket.status === "active" || ticket.status === "pending") &&
			ticket.expirationDate === null;

		const productText = hasTravelSubscription
			? "Reiseabonnement"
			: "Bedriftsbillett";

		return (
			<div className="subscription" key={ticket.id} data-test-id="ticket">
				<div
					className={`status ${ticket.status} `}
					aria-hidden="true"
					data-test-id="status-bar"
				/>
				<div data-test-id="type-value" className="type-badge">
					{getSubscriptionText(productText, isHousingAssociation)}
				</div>
				<dl>
					<dt>Status</dt>
					<dd data-test-id="status-value">
						{humanReadableStatus(ticket.status)}
					</dd>
					<dt>Billettnummer: </dt>
					<dd data-test-id="id-value">{ticket.id}</dd>
					<dt>Telefonnr: </dt>
					<dd data-test-id="phone-value">{ticket.phone}</dd>
					<dt>Gyldig fra: </dt>
					<dd data-test-id="start-date-value">
						{formatter.date.toShortDateString(ticket.startDate)}
					</dd>
					<dt>Gyldig til: </dt>
					<dd data-test-id="expiration-date-value">
						{ticket.expirationDate
							? formatter.date.toShortDateString(ticket.expirationDate)
							: "-"}
					</dd>
					<dt>Hentet: </dt>
					<dd data-test-id="picked-up-value">
						{ticket.downloadedToPhone ? "Ja" : "Nei"}
					</dd>
					<dt>Hentekode: </dt>
					<dd data-test-id="pick-up-code-value">{ticket.pickupCode}</dd>
					<dt>Pris/mnd: </dt>
					<dd data-test-id="price-per-month-value">
						{formatter.number.currency(ticket.pricePerMonth)}
					</dd>
					<dt>Soner: </dt>
					<dd data-test-id="zones-value">
						{getZonesText(ticket.nrOfZones, ticket.zones)}
					</dd>
				</dl>
				<div className="button-group">
					<button
						type="button"
						data-test-id="change-zone-button"
						disabled={!enableChangeZone}
						onClick={() => navigate(`/abonnement/${ticket.id}/endre-sone`)}
					>
						<SwapArrowLeftRight width={16} height={16} aria-hidden />
						Endre sone
					</button>
					<button
						type="button"
						data-test-id="cancel-ticket-button"
						disabled={!enableCancelTicket}
						onClick={() => navigate(`/abonnement/${ticket.id}/avslutt`)}
					>
						<Cross width={16} height={16} aria-hidden />
						Avslutt
					</button>
				</div>
			</div>
		);
	};

	const renderSubscription = (subscription: Subscription) => {
		const enableCancelSubscription =
			(subscription.state === "ACTIVE" || subscription.state === "PENDING") &&
			subscription.expireAt === null;

		return (
			<div
				className="subscription"
				key={subscription.id}
				data-test-id="subscription"
			>
				<div
					className={`status ${subscription.state.toLowerCase()} `}
					aria-hidden="true"
					data-test-id="status-bar"
				/>
				<div data-test-id="type-value" className="type-badge">
					{getSubscriptionType(subscription.type)}
				</div>
				<dl>
					<dt>Status</dt>
					<dd data-test-id="status-value">
						{humanReadableStatus(subscription.state)}
					</dd>
					<dt>Gyldig fra: </dt>
					<dd data-test-id="start-date-value">
						{formatter.date.toShortDateString(subscription.startAt)}
					</dd>
					<dt>Gyldig til: </dt>
					<dd data-test-id="expiration-date-value">
						{subscription.expireAt
							? formatter.date.toShortDateString(subscription.expireAt)
							: "-"}
					</dd>
					<dt>Pris/mnd: </dt>
					<dd data-test-id="price-per-month-value">
						{formatter.number.currency(subscription.pricePerMonth)}
					</dd>
				</dl>
				<div className="button-group">
					<button
						type="button"
						data-test-id="cancel-subscription-button"
						disabled={!enableCancelSubscription}
						onClick={() =>
							navigate(`/abonnement/${subscription.id}/avsluttabonnement`)
						}
					>
						<Cross width={16} height={16} aria-hidden />
						Avslutt abonnement
					</button>
				</div>
			</div>
		);
	};

	const renderTicketsAndSubscriptions = () => {
		return (
			<>
				{tickets.map(renderTicket)}
				{subscriptions.map(renderSubscription)}
			</>
		);
	};

	return (
		<div
			className="component-subscriptions"
			data-test-id="component-subscriptions"
		>
			<div className="header">
				<h2>Dine abonnement</h2>
			</div>
			<Button
				onClick={() => navigate("/abonnement/ny")}
				data-test-id="add-subscription-button"
				className="add-button"
				skin="primary"
				type="button"
			>
				Legg til abonnement
			</Button>

			{renderTicketsAndSubscriptions()}
		</div>
	);
};

export default SubscriptionComponent;
